import { useEffect, useRef } from 'react';

const services = [
  {
    tag: 'INDUSTRIAL MACHINERY',
    title: '산업기계설비',
    desc: '컨베이어 시스템, 자동화 생산라인, 특수 산업기계 등 고객 맞춤형 설비를 설계·제작합니다.',
    img: 'https://public.readdy.ai/ai/img_res/3dd83020ffd8a850d182c87fa9530c23.jpg',
    dark: false,
  },
  {
    tag: 'PAINTING EQUIPMENT',
    title: '도장설비',
    desc: '스프레이 부스, 건조로, 자동화 도장라인 등 최첨단 도장설비 시스템을 공급합니다.',
    img: 'https://readdy.ai/api/search-image?query=industrial%20paint%20spray%20booth%20coating%20system%20equipment%2C%20automotive%20painting%20line%2C%20enclosed%20spray%20room%2C%20professional%20coating%20machinery%2C%20clean%20industrial%20environment%2C%20dark%20background&width=480&height=360&seq=prod002&orientation=landscape',
    dark: true,
  },
  {
    tag: 'CUSTOM SOLUTION',
    title: '맞춤형 솔루션',
    desc: '고객의 생산 환경과 요구사항을 철저히 분석하여 최적의 맞춤형 설비 솔루션을 제안합니다.',
    img: 'https://readdy.ai/api/search-image?query=engineering%20design%20team%20working%20on%20industrial%20machinery%20blueprint%20cad%20design%2C%20professional%20engineers%20in%20modern%20office%2C%20technical%20drawings%20and%20plans%2C%20industrial%20engineering%20solutions%2C%20clean%20light%20background&width=480&height=360&seq=prod003&orientation=landscape',
    dark: false,
  },
];

export default function ProductsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-up').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleScroll = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="products" className="bg-white py-28" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          className="fade-up text-center mb-16"
          style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
        >
          <span className="inline-block text-xs font-bold tracking-widest text-[#C0392B] border border-[#C0392B]/30 px-3 py-1 rounded-full mb-4">
            PRODUCTS &amp; SERVICES
          </span>
          <h2 className="text-4xl lg:text-5xl font-black text-[#1C1C1C] leading-tight">
            산업기계설비 및<br />
            <span className="text-[#C0392B]">도장설비</span> 솔루션
          </h2>
          <p className="text-gray-500 mt-4 max-w-xl mx-auto leading-relaxed">
            2000년 창업 이래 25년 이상의 경험과 기술력으로 고객의 생산 현장에 최적화된 설비를 제공합니다.
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-product-shop>
          {services.map((svc, i) => (
            <div
              key={svc.title}
              className={`fade-up rounded-2xl overflow-hidden flex flex-col cursor-pointer group transition-all duration-300 hover:-translate-y-1 ${svc.dark ? 'bg-[#1A2332]' : 'bg-[#F8F6F2]'}`}
              style={{
                opacity: 0,
                transform: 'translateY(28px)',
                transition: `opacity 0.7s ease ${0.1 + i * 0.15}s, transform 0.7s ease ${0.1 + i * 0.15}s`,
              }}
              onClick={() => handleScroll(i === 0 ? 'industrial' : i === 1 ? 'painting' : 'contact')}
            >
              {/* Image */}
              <div className="w-full h-52 overflow-hidden">
                <img
                  src={svc.img}
                  alt={svc.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              {/* Content */}
              <div className="p-7 flex flex-col flex-1">
                <span className="inline-block text-xs font-bold tracking-widest text-[#C0392B] bg-[#C0392B]/10 px-3 py-1 rounded-full mb-4 self-start">
                  {svc.tag}
                </span>
                <h3 className={`text-xl font-black mb-3 ${svc.dark ? 'text-white' : 'text-[#1C1C1C]'}`}>
                  {svc.title}
                </h3>
                <p className={`text-sm leading-relaxed flex-1 ${svc.dark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {svc.desc}
                </p>
                <button
                  onClick={() => handleScroll(i === 0 ? 'industrial' : i === 1 ? 'painting' : 'contact')}
                  className={`mt-6 flex items-center gap-2 text-sm font-semibold cursor-pointer whitespace-nowrap ${svc.dark ? 'text-[#E8C27A]' : 'text-[#C0392B]'}`}
                >
                  자세히 보기
                  <i className="ri-arrow-right-line" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
